import * as React from "react"
import { useDropzone } from "react-dropzone"
import { Upload, X, FileText, Image, File } from "lucide-react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"

const FileDropzone = React.forwardRef(
  (
    {
      onFilesAccepted,
      accept,
      maxSize = 32 * 1024 * 1024, // 32MB default
      maxFiles = 1,
      disabled = false,
      className,
      children,
      ...props
    },
    ref
  ) => {
    const [files, setFiles] = React.useState([])

    const onDrop = React.useCallback(
      (acceptedFiles) => {
        setFiles(acceptedFiles)
        if (onFilesAccepted) {
          onFilesAccepted(acceptedFiles)
        }
      },
      [onFilesAccepted]
    )

    const { getRootProps, getInputProps, isDragActive, isDragReject } =
      useDropzone({
        onDrop,
        accept,
        maxSize,
        maxFiles,
        disabled,
      })

    const removeFile = (index) => {
      const newFiles = files.filter((_, i) => i !== index)
      setFiles(newFiles)
      if (onFilesAccepted) {
        onFilesAccepted(newFiles)
      }
    }

    const getFileIcon = (file) => {
      if (file.type.startsWith("image/")) {
        return <Image className="h-5 w-5 text-blue-500" />
      }
      if (file.type === "application/pdf") {
        return <FileText className="h-5 w-5 text-red-500" />
      }
      return <File className="h-5 w-5 text-gray-500" />
    }

    const formatFileSize = (bytes) => {
      if (bytes < 1024) return bytes + " B"
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB"
      return (bytes / (1024 * 1024)).toFixed(1) + " MB"
    }

    return (
      <div ref={ref} className={cn("w-full", className)} {...props}>
        <div
          {...getRootProps()}
          className={cn(
            "relative cursor-pointer rounded-lg border-2 border-dashed p-8 text-center transition-all",
            isDragActive && !isDragReject && "border-blue-500 bg-blue-50",
            isDragReject && "border-red-500 bg-red-50",
            !isDragActive &&
              "border-gray-300 bg-gray-50 hover:border-gray-400 hover:bg-gray-100",
            disabled && "cursor-not-allowed opacity-50"
          )}
        >
          <input {...getInputProps()} />

          <div className="flex flex-col items-center gap-2">
            <div
              className={cn(
                "rounded-full p-3",
                isDragActive && !isDragReject && "bg-blue-100",
                isDragReject && "bg-red-100",
                !isDragActive && "bg-gray-100"
              )}
            >
              <Upload
                className={cn(
                  "h-6 w-6",
                  isDragActive && !isDragReject && "text-blue-600",
                  isDragReject && "text-red-600",
                  !isDragActive && "text-gray-600"
                )}
              />
            </div>

            {children || (
              <>
                <p className="text-sm font-medium text-gray-700">
                  {isDragActive
                    ? isDragReject
                      ? "File type not accepted"
                      : "Drop files here"
                    : "Drag & drop files here, or click to select"}
                </p>
                <p className="text-xs text-gray-500">
                  Max size: {formatFileSize(maxSize)}
                </p>
              </>
            )}
          </div>
        </div>

        {/* File list */}
        {files.length > 0 && (
          <div className="mt-4 space-y-2">
            {files.map((file, index) => (
              <div
                key={`${file.name}-${index}`}
                className="flex items-center justify-between rounded-lg border bg-white p-3"
              >
                <div className="flex items-center gap-3">
                  {getFileIcon(file)}
                  <div>
                    <p className="text-sm font-medium text-gray-700 truncate max-w-[200px]">
                      {file.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {formatFileSize(file.size)}
                    </p>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => removeFile(index)}
                  className="h-8 w-8 text-gray-400 hover:text-red-500"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    )
  }
)
FileDropzone.displayName = "FileDropzone"

export { FileDropzone }
